import { ReactElement, useState } from "react";
import type { GetStaticProps, GetStaticPaths } from "next";
import NextHeadSeo from "next-head-seo";

import LayoutMain from "~/components/layouts/LayoutMain";
import { Header } from "~/components/app/Header";
import {
  restApiGetPostBySlugOrFallbackId,
  restApiESGetSettings,
  restApiESQuery,
} from "~/utils/restApi";
import PageMargins from "~/components/styled/PageMargins";
import DisplayAbove from "~/components/styled/DisplayAbove";
import dynamic from "next/dynamic";
import { Heading } from "~/components/ui/Heading";
import { ContentWithSidebar } from "~/components/styled/ContentWithSidebar";
import { FlexibleContent } from "~/components/flexibleContent/FlexibleContent";
import StickySidebar from "~/components/sidebars/StickySidebar";
import SubMenuTwoCol from "~/components/styled/SubMenuTwoCol";
import { SidebarShare } from "~/components/sidebars/SidebarShare";
import { useConfigContext } from "~/providers/ConfigContextProvider";
import { appConfig } from "~/config";
import styled from "styled-components";
import SafeHtmlSpan from "~/components/ui/SafeHtmlSpan";
import { SvgBackground } from "~/components/ui/SvgBackground";
import Button from "~/components/styled/Button";
import { RelatedContent } from "~/components/content/RelatedContent";
import { RelatedProducts } from "~/components/content/RelatedProducts";

import { ErrorLock } from "~/components/app/ErrorLock";

const VideoPlayer = dynamic(() => import("~/components/ui/Player/VideoPlayer"));

const VideoContainer = styled.div`
  background-color: #000;
  position: relative;
  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        height: 100vh;
        width: 100%;
        max-height: calc((100vw - var(--sbw, 0)) / 16 * 9);      
        `;
    })}
`;

const Overlay = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.1), rgba(0, 0, 0, 0.5));
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
`;

const TitleGrid = styled.div`
  display: grid;
  align-items: end;
  ${(props) =>
    props.theme.apply(["base", "mobile", "tablet"], (breakpoint: string) => {
      return `
          grid-template-columns: 1fr ${props.theme.spacePx(breakpoint, 5)};
          column-gap: ${props.theme.spacePx(breakpoint, 6)};          
        `;
    })}

  ${(props) =>
    props.theme.apply(
      ["tabletLandscape", "desktop", "screen"],
      (breakpoint: string) => {
        return `
          grid-template-columns: 1fr ${props.theme.spacePx(breakpoint, 5)};
          column-gap: ${props.theme.spacePx(breakpoint, 6)};          
        `;
      }
    )}
  transition: opacity 0.5s;
`;

const Title = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  color: #fff;
  opacity: 0;

  ${({ theme }) => theme.breakpoints.tablet} {
    opacity: 1;
  }
`;

const Info = styled.div`
  display: flex;

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
          padding-bottom: ${(
            props.theme.space(breakpoint, 7) - props.theme.space(breakpoint, 9)
          ).toFixed(2)}px;          
        `;
    })}
`;

const SlideshowButton = styled(Button)`
  transition: filter 0.5s;
  pointer-events: all;
  filter: invert(100%);

  ${(props) =>
    props.theme.apply("default", (breakpoint: string) => {
      return `
        width: ${props.theme.spacePx(breakpoint, 5)};
        height: ${props.theme.spacePx(breakpoint, 5)};
        margin-bottom:${props.theme.spacePx(breakpoint, 6)};
        `;
    })}
`;

const VideoDetail = ({ data }: { data: any }) => {
  const config = useConfigContext();

  const [showHeader, setShowHeader] = useState(true);

  const type = data?.taxonomies?.media_category?.[0]?.name ?? "Video";

  const hasVideo =
    (data?.acf.type === "vimeo_api" && data?.acf.vimeo_api?.files) ||
    data?.acf.url;

  const previewComponent: any = (
    <Overlay>
      <PageMargins>
        <div>
          <TitleGrid>
            <Title>
              <Info>
                <SafeHtmlSpan html={type} />
                &emsp;
                <span style={{ fontWeight: 400 }}>
                  <SafeHtmlSpan html={data.acf.duration} />
                </span>
              </Info>
              <Heading space={6} heading="h1" asTag html={data?.title} />
            </Title>
            <SlideshowButton aria-label="play video">
              <SvgBackground width="100%" height="100%" type="play" />
            </SlideshowButton>
          </TitleGrid>
        </div>
      </PageMargins>

      <div></div>
    </Overlay>
  );

  return (
    <>
      <NextHeadSeo
        canonical={data?.yoast_head_json?.canonical}
        title={
          data?.yoast_head_json?.title ??
          `${data?.title} - Video - Media - ${appConfig.appTitle}`
        }
        description={data?.yoast_head_json?.description}
        og={{
          title: data?.yoast_head_json?.og_title,
          type: data?.yoast_head_json?.og_type,
          siteName: data?.yoast_head_json?.og_site_name,
          image: data?.yoast_head_json?.twitter_image,
        }}
        twitter={{
          card: "summary_large_image",
        }}
      />
      <Header hasHero>
        <SubMenuTwoCol width="auto" alignSelf="center" colGapSpace={8}>
          <Heading space={7} heading="h3" inline trimmed html={data?.title} />

          <DisplayAbove breakpoint="tabletLandscape" inline>
            <Heading space={0} heading="h4" inline>
              <SafeHtmlSpan html={type} />
              &emsp;
              <span style={{ fontWeight: 400 }}>
                <SafeHtmlSpan html={data.acf.duration} />
              </span>
            </Heading>
          </DisplayAbove>
        </SubMenuTwoCol>
      </Header>
      <Header isOverlay isHidden={!showHeader} />
      <main id="content">
        {hasVideo && (
          <VideoContainer>
            <ErrorLock>
              {data?.acf?.type === "vimeo_api" && (
                <VideoPlayer
                  mode="fill"
                  src={data?.acf?.vimeo_api?.files}
                  captions={data?.acf?.vimeo_api?.captions}
                  vimeoId={data?.acf?.vimeo_api?.vimeo_id}
                  posterSizes={
                    data?.acf?.poster?.sizes ??
                    data?.acf?.vimeo_api?.poster?.sizes
                  }
                  type={data.type}
                  options={{
                    onPlay: () => {
                      if (
                        data.type === "youtube_url" ||
                        data.type === "vimeo_url"
                      )
                        setShowHeader(false);
                    },
                    onPause: () => {
                      if (
                        data.type === "youtube_url" ||
                        data.type === "vimeo_url"
                      )
                        setShowHeader(true);
                    },
                    onShowUi: () => {
                      setShowHeader(true);
                    },
                    onHideUi: () => {
                      setShowHeader(false);
                    },
                  }}
                  previewComponent={previewComponent}
                />
              )}
              {(data?.acf?.type === "youtube_url" ||
                data?.acf?.type === "vimeo_url") && (
                <VideoPlayer
                  mode="fill"
                  src={data?.acf?.url.replace("manage/videos/", "")}
                  type={data?.acf?.type}
                  previewComponent={previewComponent}
                />
              )}
            </ErrorLock>
          </VideoContainer>
        )}

        <PageMargins spaceTop={9} spaceBottom={5}>
          <Heading space={7} heading="h4">
            <SafeHtmlSpan html={type} />
            &emsp;
            <span style={{ fontWeight: 400 }}>
              <SafeHtmlSpan html={data.acf.duration} />
            </span>
          </Heading>

          <Heading
            asTag
            heading="h1"
            space={data?.acf?.sub_title?.trim() ? 8 : 6}
            html={data?.title}
          />

          {data?.acf?.sub_title?.trim() && (
            <Heading
              heading="h2"
              space={6}
              html={data?.acf?.sub_title?.trim()}
            />
          )}

          <ContentWithSidebar>
            {data?.acf?.content?.length > 0 ? (
              <FlexibleContent
                columnWidth="full"
                field="content"
                content={data.acf.content}
              />
            ) : (
              <div />
            )}
            <StickySidebar>
              <SidebarShare
                title={data?.title}
                url={`${config.baseUrl}/media/video/${data?.slug}`}
              />
            </StickySidebar>
          </ContentWithSidebar>
        </PageMargins>
        <RelatedProducts
          key={`video-products-${data?.id}`}
          id={data?.id}
          ids={data?.acf?.shop_items ?? []}
        />
        <RelatedContent
          key={`video-related-${data?.id}`}
          ids={[data?.id]}
          relatedItems={data?.acf?.related_content ?? []}
        />
      </main>
    </>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const events = await restApiESQuery({
    type: "video",
    perPage: 100,
    orderby: "post_date",
    order: "desc",
  });

  return {
    paths: events?.data?.length
      ? events?.data.map((item: any) => item.url)
      : [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (context) => {
  const token = (context?.previewData as any)?.token;

  const data = await restApiGetPostBySlugOrFallbackId(
    "video",
    context?.params?.slug,
    token
  );

  const frontendSettings = await restApiESGetSettings(["mediaCategory"]);
  if (!data)
    return {
      props: {
        frontendSettings,
      },
      notFound: true,
      revalidate: frontendSettings?.options?.copyrightNotice ? 240 : 15,
    };

  return {
    props: {
      frontendSettings,
      data,
    },
    revalidate: frontendSettings?.options?.copyrightNotice
      ? appConfig.revalidateInterval("videoDetail", {
          date: data?.date,
          modified: data?.modified,
        })
      : 15,
  };
};

VideoDetail.getLayout = function getLayout(page: ReactElement, props: any) {
  return <LayoutMain props={props}>{page}</LayoutMain>;
};
export default VideoDetail;
